import { getAlmaAPIUrlBasedOnEnv, getVersion } from "../helpers";
import { ENV } from "../types";

export type Installment = {
  due_date: number;
  purchase_amount: number;
  customer_fee: number;
  customer_interest: number;
};

export type EligiblePlan = {
  eligible: boolean;
  deferred_days: number;
  deferred_months: number;
  purchase_amount: number;
  customer_fee: number;
  customer_interest: number;
  payment_plan: Installment[];
};

type EligibilityQuery = {
  merchantId: string;
  amountInCents: number;
  installmentsCount: number;
  deferredDays: number;
  deferredMonths: number;
  captureMethod: string;
  locale: string;
};

// fetch rejects only when the request can not be sent: blocked (CSP, ad blocker…) or network error
export class BlockedRequestError extends Error {
  name = "BlockedRequestError";
}

export async function fetchEligiblePlan(
  query: EligibilityQuery,
  env: ENV
): Promise<EligiblePlan | null> {
  const response = await fetch(
    `${getAlmaAPIUrlBasedOnEnv(env)}/v2/payments/eligibility`,
    {
      method: "POST",
      headers: {
        Authorization: `Alma-Merchant-Auth ${query.merchantId}`,
        "Content-Type": "application/json",
        "X-Alma-Agent": `Alma Checkout - InPage/${getVersion()}`,
      },
      body: JSON.stringify({
        purchase_amount: query.amountInCents,
        queries: [
          {
            installments_count: query.installmentsCount,
            deferred_days: query.deferredDays,
            deferred_months: query.deferredMonths,
          },
        ],
        capture_method: query.captureMethod,
        locale: query.locale,
      }),
    }
  ).catch((error) => {
    throw new BlockedRequestError(String(error));
  });

  if (!response.ok) {
    throw new Error(`Eligibility request failed with status ${response.status}`);
  }

  const plans: EligiblePlan[] = await response.json();

  return (
    plans.find(
      (plan) =>
        plan.eligible &&
        plan.deferred_days === query.deferredDays &&
        plan.deferred_months === query.deferredMonths
    ) ?? null
  );
}
